import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
} from 'react';
import {
  Platform,
  Pressable,
  StyleProp,
  View,
  type AccessibilityRole,
  type AccessibilityState,
  type ViewStyle,
} from 'react-native';
import { Gesture, GestureDetector } from 'react-native-gesture-handler';
import { runOnJS, useSharedValue, type SharedValue } from 'react-native-reanimated';
import type { HapticType } from '../../shared/utils/haptics';
import { triggerHaptic } from '../../shared/utils/haptics';

export type { HapticType };

export type Clickable2HitSlop = number | { top?: number; left?: number; right?: number; bottom?: number };

export type Clickable2PressState = {
  /** UI-thread 0 → 1 press depth for Reanimated styles / worklets. */
  pressed: SharedValue<number>;
  /**
   * Deprecated JS mirror — always `false`.
   * Press visuals must use `pressed` (SharedValue) so scroll starts do not re-render.
   */
  isPressed: boolean;
  disabled: boolean;
};

export type Clickable2Props = {
  children: React.ReactNode | ((state: Clickable2PressState) => React.ReactNode);
  onPress?: () => void;
  onPressIn?: () => void;
  onLongPress?: () => void;
  delayLongPress?: number;
  disabled?: boolean;
  style?: StyleProp<ViewStyle>;
  haptics?: boolean;
  hapticType?: HapticType;
  hapticOnDisabledPress?: boolean;
  hitSlop?: Clickable2HitSlop;
  accessibilityLabel?: string;
  accessibilityRole?: AccessibilityRole;
  accessibilityState?: AccessibilityState;
  testID?: string;
};

const Clickable2PressContext = createContext<Clickable2PressState | null>(null);

/** Nested Clickable2 claims the parent touch so pressIn/press do not double-fire. */
type Clickable2NestGate = {
  claimFromChild: () => void;
};

const Clickable2NestContext = createContext<Clickable2NestGate | null>(null);

export function useClickable2Press(): Clickable2PressState {
  const ctx = useContext(Clickable2PressContext);
  if (ctx == null) {
    throw new Error('useClickable2Press must be used inside Clickable2');
  }
  return ctx;
}

const MAX_GESTURE_DISTANCE = 25;
const TAP_MAX_DURATION_MS = 120_000;

function toHitSlop(hit_slop: Clickable2HitSlop | undefined) {
  if (hit_slop == null) {
    return undefined;
  }
  if (typeof hit_slop === 'number') {
    return { top: hit_slop, left: hit_slop, right: hit_slop, bottom: hit_slop };
  }
  return {
    top: hit_slop.top ?? 0,
    left: hit_slop.left ?? 0,
    right: hit_slop.right ?? 0,
    bottom: hit_slop.bottom ?? 0,
  };
}

export function Clickable2({
  children,
  onPress,
  onPressIn,
  onLongPress,
  delayLongPress = 500,
  disabled = false,
  style,
  haptics = false,
  hapticType = 'soft',
  hapticOnDisabledPress = true,
  hitSlop,
  accessibilityLabel,
  accessibilityRole = 'button',
  accessibilityState: accessibilityStateProp,
  testID,
}: Clickable2Props) {
  const pressed = useSharedValue(0);
  const disabled_sv = useSharedValue(disabled ? 1 : 0);
  const hit_slop_insets = useMemo(() => toHitSlop(hitSlop), [hitSlop]);
  const parent_nest = useContext(Clickable2NestContext);
  const child_claimed_ref = useRef(false);
  const has_press_in = onPressIn != null;
  const has_press = onPress != null;
  const has_long_press = onLongPress != null;
  const has_parent_nest = parent_nest != null;

  useEffect(() => {
    disabled_sv.value = disabled ? 1 : 0;
  }, [disabled, disabled_sv]);

  const haptic_ref = useRef({ hapticType, haptics, hapticOnDisabledPress });
  haptic_ref.current = { hapticType, haptics, hapticOnDisabledPress };

  const claimFromChild = useCallback(() => {
    child_claimed_ref.current = true;
  }, []);

  const nest_gate = useMemo<Clickable2NestGate>(() => ({ claimFromChild }), [claimFromChild]);

  const clearNestClaim = useCallback(() => {
    child_claimed_ref.current = false;
  }, []);

  const claim_parent_nest = useCallback(() => {
    parent_nest?.claimFromChild();
  }, [parent_nest]);

  const fireHaptic = useCallback((kind: 'success' | 'disabled') => {
    if (kind === 'success' && haptic_ref.current.haptics) {
      triggerHaptic(haptic_ref.current.hapticType);
    } else if (kind === 'disabled' && haptic_ref.current.hapticOnDisabledPress) {
      triggerHaptic('rigid');
    }
  }, []);

  const on_js_press = useCallback(() => {
    if (child_claimed_ref.current) {
      return;
    }
    onPress?.();
  }, [onPress]);

  const on_js_press_in = useCallback(() => {
    parent_nest?.claimFromChild();
    if (onPressIn == null) {
      return;
    }
    // Defer so a nested Clickable2 can claim this touch before we commit.
    Promise.resolve().then(() => {
      if (child_claimed_ref.current) {
        return;
      }
      onPressIn();
    });
  }, [onPressIn, parent_nest]);

  const on_js_long = useCallback(() => {
    if (child_claimed_ref.current) {
      return;
    }
    onLongPress?.();
  }, [onLongPress]);

  const gesture = useMemo(() => {
    let tap = Gesture.Tap()
      .maxDuration(TAP_MAX_DURATION_MS)
      .maxDistance(MAX_GESTURE_DISTANCE)
      .onBegin(() => {
        'worklet';
        if (disabled_sv.value > 0.5) {
          return;
        }
        pressed.value = 1;
        // Keep press on the UI thread only — never setState here (scroll starts would re-render).
        if (has_press_in) {
          runOnJS(on_js_press_in)();
        } else if (has_parent_nest) {
          runOnJS(claim_parent_nest)();
        }
      })
      .onEnd((_, success) => {
        'worklet';
        if (!success) {
          return;
        }
        if (disabled_sv.value > 0.5) {
          runOnJS(fireHaptic)('disabled');
          return;
        }
        runOnJS(fireHaptic)('success');
        if (has_press) {
          runOnJS(on_js_press)();
        }
      })
      .onFinalize(() => {
        'worklet';
        pressed.value = 0;
        runOnJS(clearNestClaim)();
      })
      .enabled(has_press || has_press_in);

    if (hit_slop_insets) {
      tap = tap.hitSlop(hit_slop_insets);
    }

    if (!has_long_press) {
      return tap;
    }

    let long_press = Gesture.LongPress()
      .minDuration(delayLongPress)
      .maxDistance(MAX_GESTURE_DISTANCE)
      .onStart(() => {
        'worklet';
        if (disabled_sv.value > 0.5) {
          return;
        }
        runOnJS(on_js_long)();
      });

    if (hit_slop_insets) {
      long_press = long_press.hitSlop(hit_slop_insets);
    }

    if (!has_press && !has_press_in) {
      return long_press;
    }

    return Gesture.Race(long_press, tap);
  }, [
    claim_parent_nest,
    clearNestClaim,
    delayLongPress,
    disabled_sv,
    fireHaptic,
    has_long_press,
    has_parent_nest,
    has_press,
    has_press_in,
    hit_slop_insets,
    on_js_long,
    on_js_press,
    on_js_press_in,
    pressed,
  ]);

  const accessibility_state: AccessibilityState = {
    ...accessibilityStateProp,
    disabled: disabled || !!accessibilityStateProp?.disabled,
  };

  const press_state = useMemo<Clickable2PressState>(
    () => ({ pressed, isPressed: false, disabled }),
    [pressed, disabled],
  );

  const content =
    typeof children === 'function' ? children(press_state) : children;

  // Web: Pressable path — gesture-handler Tap.onEnd(success) is stubbed on PWA.
  if (Platform.OS === 'web') {
    return (
      <Pressable
        testID={testID}
        disabled={disabled || !onPress}
        hitSlop={hitSlop}
        accessible
        accessibilityLabel={accessibilityLabel}
        accessibilityRole={accessibilityRole}
        accessibilityState={accessibility_state}
        onPress={() => {
          if (disabled) {
            return;
          }
          fireHaptic('success');
          onPress?.();
        }}
        onPressIn={() => {
          if (disabled) {
            return;
          }
          pressed.value = 1;
          onPressIn?.();
        }}
        onPressOut={() => {
          pressed.value = 0;
          clearNestClaim();
        }}
        onLongPress={
          onLongPress
            ? () => {
                if (disabled) {
                  return;
                }
                fireHaptic('success');
                onLongPress();
              }
            : undefined
        }
        delayLongPress={delayLongPress}
        style={[style, disabled ? { opacity: 0.5 } : null]}>
        <Clickable2NestContext.Provider value={nest_gate}>
          <Clickable2PressContext.Provider value={press_state}>
            {content}
          </Clickable2PressContext.Provider>
        </Clickable2NestContext.Provider>
      </Pressable>
    );
  }

  return (
    <GestureDetector gesture={gesture}>
      <View
        testID={testID}
        collapsable={false}
        accessible
        accessibilityLabel={accessibilityLabel}
        accessibilityRole={accessibilityRole}
        accessibilityState={accessibility_state}
        style={[style, disabled ? { opacity: 0.5 } : null]}>
        <Clickable2NestContext.Provider value={nest_gate}>
          <Clickable2PressContext.Provider value={press_state}>
            {content}
          </Clickable2PressContext.Provider>
        </Clickable2NestContext.Provider>
      </View>
    </GestureDetector>
  );
}

export default Clickable2;
